import type { OrderAppOrderType } from '@/graphql/orderApp/api';
import type {
  CountryCode,
  MetadataItem,
  Money,
  OrderFragment,
} from '@/graphql/saleor/api';
import { getMetaValue, parseComplexMetadata } from '@/lib/metadata/helpers';
import type { Maybe } from '@/lib/types';

import type { SALEOR_PRE_ORDER_CONFIG_KEY } from './const';
import { SALEOR_PRODUCT_VARIANT_METADATA } from './metadata/const';

export type PreOrderData = {
  deposit: number;
  deposit_net: number;
  deposit_percentage: number;
};

export const isPreOrderType = (order: Maybe<OrderFragment>) =>
  (order?.orderType as OrderAppOrderType) === 'PRE_ORDER' &&
  !!order?.preOrderData;

export const extractPreorderData = (order: Maybe<OrderFragment>) => {
  const isPreOrder = isPreOrderType(order);

  if (isPreOrder) {
    return parseComplexMetadata(order!.preOrderData) as PreOrderData;
  }

  return null;
};

export const isOrderDepositPaid = (order: Maybe<OrderFragment>) => {
  const preOrderData = extractPreorderData(order);

  if (preOrderData && order) {
    const deposit = preOrderData.deposit;
    const isDepositAuthorized =
      order.totalAuthorized.amount > 0 &&
      deposit >= order.totalAuthorized.amount;
    const isDepositCharged =
      order.totalCharged.amount > 0 && deposit >= order.totalCharged.amount;

    // If it's still pending we don't have access to pending charge and authorized amounts (saleor...)
    const isAbsBalanceLessThanOrder =
      Math.abs(order.totalBalance.amount) < order.total.gross.amount;

    if (isDepositCharged || isDepositAuthorized || isAbsBalanceLessThanOrder) {
      return true;
    }
  }

  return false;
};

export const extractBalance = (order: Maybe<OrderFragment>) => {
  return {
    ...order?.totalBalance,
    amount: Math.abs(order?.totalBalance?.amount ?? 0),
  } as Money;
};

type SaleorPreOrderConfig = {
  [key in SALEOR_PRE_ORDER_CONFIG_KEY]: any;
};
export const getPreOrderConfig = (
  metadata: readonly MetadataItem[],
  key: SALEOR_PRE_ORDER_CONFIG_KEY,
  countryCode: CountryCode,
) => {
  const preOrderConfigList: SaleorPreOrderConfig[] = JSON.parse(
    getMetaValue(metadata, SALEOR_PRODUCT_VARIANT_METADATA.preOrderConfig) ??
      '[]',
  );
  return preOrderConfigList.find(
    (config: SaleorPreOrderConfig) => config.market === countryCode,
  )?.[key];
};
